/**
 * ShareFilterDateRange
 *
 * Pair of date inputs working on ISO `yyyy-MM-dd` strings (the wire format of
 * share filter date bounds). Locale-aware display format, clearable, and
 * mutually clamped (from ≤ to).
 */
import { memo, useMemo } from 'react';
import { Group } from '@mantine/core';
import { DateInput, type DateValue } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getDateInputPattern } from '@/config/dateLocales';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { dateToISOString } from '@/utils/dateHelpers';

interface ShareFilterDateRangeProps {
  fromLabel: string;
  toLabel: string;
  fromValue?: string;
  toValue?: string;
  onFromChange: (iso?: string) => void;
  onToChange: (iso?: string) => void;
}

/** Converts a DateInput change value to the ISO `yyyy-MM-dd` wire format. */
function toIso(value: DateValue): string | undefined {
  return dateToISOString(value) ?? undefined;
}

const POPOVER_PROPS = {
  zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
  withinPortal: true,
};

export const ShareFilterDateRange = memo(function ShareFilterDateRange({
  fromLabel,
  toLabel,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
}: ShareFilterDateRangeProps) {
  const { i18n } = useTranslation();
  const dateFormat = useMemo(
    () => getDateInputPattern(i18n.language).pattern,
    [i18n.language]
  );

  return (
    <Group grow>
      <DateInput
        label={fromLabel}
        placeholder={dateFormat}
        valueFormat={dateFormat}
        value={fromValue ?? null}
        onChange={(value) => onFromChange(toIso(value))}
        leftSection={<IconCalendar size={ICON_SIZE_SM} />}
        clearable
        maxDate={toValue}
        popoverProps={POPOVER_PROPS}
      />
      <DateInput
        label={toLabel}
        placeholder={dateFormat}
        valueFormat={dateFormat}
        value={toValue ?? null}
        onChange={(value) => onToChange(toIso(value))}
        leftSection={<IconCalendar size={ICON_SIZE_SM} />}
        clearable
        minDate={fromValue}
        popoverProps={POPOVER_PROPS}
      />
    </Group>
  );
});
